import { Locales } from "adapters/i18n";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(duration);

export const DATE_FORMATS = {
  "DD-MM-YYYY": "DD-MM-YYYY",
  "DD/MM/YYYY": "DD/MM/YYYY",
  "MM-DD-YYYY": "MM-DD-YYYY",
  "MM/DD/YYYY": "MM/DD/YYYY",
  "YYYY-MM-DD": "YYYY-MM-DD",
  "YYYY-MM-DD HH:mm:ss": "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DDThh:mm:ss": "YYYY-MM-DDTHH:mm:ss",
} as const;

export const HOUR_FORMATS = {
  "HH:mm": "HH:mm",
  "HH:mm:ss": "HH:mm:ss",
} as const;

export const DATE_UNIT = {
  MILLISECOND: "millisecond",
  SECOND: "second",
  MINUTE: "minute",
  HOUR: "hour",
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
  YEAR: "year",
  DATE: "date",
} as const;

export type DateFormat = (typeof DATE_FORMATS)[keyof typeof DATE_FORMATS];
export type DateUnit = (typeof DATE_UNIT)[keyof typeof DATE_UNIT];
export type DateValue =
  | string
  | number
  | Date
  | Dayjs
  | null
  | undefined
  | DateInstance;

function unwrapDateValue(
  value: DateValue
): string | number | Date | Dayjs | null | undefined {
  if (value instanceof DateInstance) {
    return value.toDate();
  }
  return value;
}

class DateInstance {
  private instance: Dayjs;

  constructor(value?: DateValue, valueFormat?: DateFormat) {
    const unwrapped = unwrapDateValue(value);
    this.instance = valueFormat
      ? dayjs(unwrapped, valueFormat)
      : dayjs(unwrapped);
  }

  static isInstance(value: unknown): value is DateInstance {
    return value instanceof DateInstance;
  }

  static toInstance(value: DateInstance | DateValue): Dayjs {
    return DateInstance.isInstance(value)
      ? value.instance
      : dayjs(unwrapDateValue(value));
  }

  static duration(time: number) {
    return dayjs.duration(time);
  }

  // #region: Comparison methods
  isBefore(
    otherDate: DateValue,
    unit: DateUnit = DATE_UNIT.MILLISECOND
  ): boolean {
    const compareDate = DateInstance.toInstance(otherDate);
    return this.instance.isBefore(compareDate, unit);
  }

  isAfter(
    otherDate: DateValue,
    unit: DateUnit = DATE_UNIT.MILLISECOND
  ): boolean {
    const compareDate = DateInstance.toInstance(otherDate);
    return this.instance.isAfter(compareDate, unit);
  }

  isSame(
    otherDate: DateValue,
    unit: DateUnit = DATE_UNIT.MILLISECOND
  ): boolean {
    const compareDate = DateInstance.toInstance(otherDate);
    return this.instance.isSame(compareDate, unit);
  }

  isSameOrAfter(
    otherDate: DateValue,
    unit: DateUnit = DATE_UNIT.MILLISECOND
  ): boolean {
    return this.isSame(otherDate, unit) || this.isAfter(otherDate, unit);
  }

  isSameOrBefore(
    otherDate: DateValue,
    unit: DateUnit = DATE_UNIT.MILLISECOND
  ): boolean {
    return this.isSame(otherDate, unit) || this.isBefore(otherDate, unit);
  }
  // #endregion

  // #region: Date manipulation methods
  add(value: number, unit: DateUnit): DateInstance {
    if (unit === DATE_UNIT.DATE) {
      throw new Error("Cannot manipulate date with target unit being 'date'.");
    }
    return new DateInstance(this.instance.add(value, unit));
  }

  subtract(value: number, unit: DateUnit): DateInstance {
    if (unit === DATE_UNIT.DATE) {
      throw new Error("Cannot manipulate date with target unit being 'date'.");
    }
    return new DateInstance(this.instance.subtract(value, unit));
  }
  // #endregion

  // #region: Getters
  year(): number {
    return this.instance.year();
  }

  month(): number {
    return this.instance.month();
  }

  monthName(locale: Locales): string {
    const date = this.instance.toDate();
    const month = date.toLocaleString(locale, { month: "long" });
    return month;
  }
  // #endregion

  // #region start/End of period methods
  startOf(unit: DateUnit) {
    return this.instance.startOf(unit);
  }

  endOf(unit: DateUnit) {
    return this.instance.endOf(unit);
  }

  diff(value: DateValue, unit: DateUnit): number {
    return this.instance.diff(unwrapDateValue(value), unit);
  }
  // #endregion

  // #region: Formatting methods
  format(format: DateFormat = DATE_FORMATS["YYYY-MM-DD"]): string {
    return this.instance.format(format);
  }

  toISOString(): string {
    return this.instance.toISOString();
  }
  // #endregion

  // #region: Timezone related methods
  utcOffset(): number;
  utcOffset(offset: string | number): DateInstance;
  utcOffset(offset?: string | number): number | DateInstance {
    if (offset === undefined) {
      return this.instance.utcOffset();
    }
    // Set UTC offset and return new instance
    return new DateInstance(this.instance.utcOffset(offset));
  }
  // #endregion

  // #region: Parser methods
  toDate(): Date {
    return this.instance.toDate();
  }

  clone(): DateInstance {
    return new DateInstance(this.instance);
  }

  valueOf(): number {
    return this.instance.valueOf();
  }
  // #endregion

  // #region: Validations
  isValid(): boolean {
    return this.instance.isValid();
  }
  // #endregion
}

/**
 * Singleton date util class
 */
export const DateUtils = {
  parse: function (value?: DateValue, valueFormat?: DateFormat) {
    return DateInstance.isInstance(value)
      ? (value.clone() as DateInstance)
      : new DateInstance(value, valueFormat);
  },

  now: function () {
    return new DateInstance();
  },

  utc: function (dateInput?: DateValue) {
    return new DateInstance(dayjs.utc(unwrapDateValue(dateInput)));
  },

  withUtcOffset: function (dateInput: DateValue, offset: string | number) {
    return new DateInstance(
      dayjs(unwrapDateValue(dateInput)).utcOffset(offset)
    );
  },

  duration: function (time: number) {
    return DateInstance.duration(time);
  },
  isDateLike: function (value: unknown) {
    if (value instanceof Date) return true;
    if (typeof value === "string") {
      return /^\d{2}\/\d{2}\/\d{4}$/.test(value);
    }
    return false;
  },
};
